import type { CSSProperties } from "react";

import lightUrl from "../assets/valubrief-logo.svg";
import darkUrl from "../assets/valubrief-logo-dark.svg";

/**
 * THE MARK, IN BOTH SCHEMES (2026-09-01, W3-UIFIX2).
 *
 * WHAT WAS WRONG. Every dark after-image of the W2 pass shows it: the app bar carries
 * `<img src={valubrief-logo.svg}>`, whose six wordmark paths are a navy ramp from #91a0ac down to
 * #1e3c54. On the dark bar (`--vb-color-surface`, #161F2A) the two darkest of those measure 1.35:1
 * and 1.11:1 — the mark is not dim, it is absent, on every dark screen in the product. W2 found
 * this and deliberately left it, with the right reason: "it needs a dark lockup, not a CSS filter
 * I picked."
 *
 * WHY NOT A `filter`. `invert()` / `brightness()` apply to the WHOLE image, so the one path that is
 * not navy — the brand green, #1E7A3D — comes out as whatever the arithmetic makes of it. A filter
 * cannot recolour six paths one way and leave a seventh alone, so using one means throwing the
 * green away and calling the result the brand. That is deriving a brand colour in passing, which
 * `design-tokens.ts` exists to prevent.
 *
 * WHY NOT AN INLINE `<svg>` DRIVEN BY `currentColor`. That was the first choice and it does not
 * survive contact with the asset. The lockup is SEVEN paths in SIX different navies plus a green —
 * it is a ramp, not a silhouette — so `currentColor` would flatten it to one ink and lose the shape
 * it is drawn with. Inlining it with seven `var()` fills would work, but the path data is 56KB that
 * would have to be re-emitted into a `.tsx`, and `AppShell.test.tsx` reads the mark's rendered
 * height off `img[alt="ValuBrief"]`, which an inline `<svg>` would break for no gain to the user.
 *
 * SO: A SECOND ASSET, AND THE CASCADE CHOOSES. `valubrief-logo-dark.svg` is the same file with the
 * seven fills substituted — same paths, same viewBox, same geometry, byte-identical otherwise.
 * Both `<img>`s render; `app.css` shows exactly one, under the same three-block rule every other
 * colour in this app follows (bare `:root`, the `prefers-color-scheme` block guarded against an
 * explicit light choice, and `[data-theme="dark"]`). No JavaScript reads the theme, so the mark
 * cannot disagree with the page it sits on and there is no flash on first paint.
 *
 * HOW THE DARK RAMP WAS DERIVED, so it is not a colour somebody liked:
 *
 *   hue and saturation are the light lockup's own, unchanged, per path;
 *   lightness is mirrored WITHIN the ramp (the darkest step becomes the lightest), so the mark's
 *     internal ordering — which path reads as the wordmark and which as the graphic behind it —
 *     survives the inversion instead of collapsing;
 *   the whole ramp is then lifted by ONE constant, chosen so the wordmark ink lands exactly on the
 *     dark scheme's already-ratified `--vb-color-text` lightness (#E8EDF4). Nothing new is decided:
 *     the mark's ink is the page's ink.
 *
 * Measured against the dark bar (#161F2A): 4.66 · 6.43 · 7.49 · 10.24 · 12.35 · 14.28. The lowest
 * step clears 4.5:1, which is the floor `app.css`'s dark block sets for itself.
 *
 * THE GREEN IS NOT DERIVED HERE AT ALL. It becomes `--vb-brand-green`'s dark value, #6FCB92 —
 * ratified in `app.css` on 2026-09-01 and already the green every other dark surface uses. One
 * claim, one colour, in both schemes.
 *
 * FLAGGED FOR THE OWNER rather than presented as settled: a dark lockup is a decision about a brand
 * mark, and this one is derived rather than drawn. If the brand has a real dark lockup, replacing
 * `assets/valubrief-logo-dark.svg` is the whole change — nothing else moves.
 */
export function BrandMark({ height, style }: { height: number; style?: CSSProperties }) {
  return (
    <>
      {/* The light one keeps `alt` and the inline height: it is the mark, and it is what a screen
          reader and `AppShell.test.tsx` both read. */}
      <img className="vb-mark-light" src={lightUrl} alt="ValuBrief" style={{ height, ...style }} />
      {/* The dark one is the same mark drawn for a dark ground, so it is decorative to assistive
          technology — announcing "ValuBrief" twice would be the accessibility cost of a colour
          scheme. */}
      <img
        className="vb-mark-dark"
        src={darkUrl}
        alt=""
        aria-hidden="true"
        style={{ height, ...style }}
      />
    </>
  );
}
